/**
 * Handles first-party persistence of attribution data.
 * Full localStorage payloads are preferred when equally fresh.
 * A bounded cookie provides a fallback when localStorage is unavailable.
 */

var AttributionStorage = (function () {
  'use strict';

  var ATTR_DEBUG = false;
  var COOKIE_NAME = '_attr_payload';
  var COOKIE_DAYS = 30;
  // Leave room for the cookie name, expiry and other attributes below 4 KB.
  var COOKIE_VALUE_LIMIT = 3800;
  var lastWriteAt = 0;

  function log() {
    if (ATTR_DEBUG) console.log.apply(console, arguments);
  }

  function getCookie(name) {
    try {
      var prefix = name + '=';
      var cookies = document.cookie.split(';');
      for (var i = 0; i < cookies.length; i++) {
        var cookie = cookies[i].trim();
        if (cookie.indexOf(prefix) === 0) {
          return decodeURIComponent(cookie.substring(prefix.length));
        }
      }
    } catch (e) {
      log('[Attribution][Storage] Cookie unavailable or malformed');
    }
    return null;
  }

  function parsePayload(value) {
    if (!value) return null;
    try {
      var payload = JSON.parse(value);
      return payload && typeof payload === 'object' && !Array.isArray(payload)
        ? payload : null;
    } catch (e) {
      return null;
    }
  }

  function getCandidates() {
    var local = null;
    try {
      local = parsePayload(localStorage.getItem(COOKIE_NAME));
    } catch (e) {}
    return { local: local, cookie: parsePayload(getCookie(COOKIE_NAME)) };
  }

  function payloadTime(payload) {
    var time = Date.parse(payload.updated_at || '');
    return isFinite(time) ? time : -Infinity;
  }

  function writeTime(payload) {
    var value = payload && payload.storage_written_at;
    return typeof value === 'number' && isFinite(value) ? value : 0;
  }

  function read() {
    var candidates = getCandidates();
    var local = candidates.local;
    var cookie = candidates.cookie;
    if (!local) return cookie;
    if (!cookie) return local;

    // New writes advance past both stored sequences, even after a clock
    // rollback. Prefer that ordering over a stale future-dated updated_at.
    if (writeTime(local) !== writeTime(cookie)) {
      return writeTime(local) > writeTime(cookie) ? local : cookie;
    }
    // Legacy payloads have no write sequence; retain timestamp comparison.
    var localTime = payloadTime(local);
    var cookieTime = payloadTime(cookie);
    if (localTime !== cookieTime) return localTime > cookieTime ? local : cookie;
    if (local.storage_compacted && !cookie.storage_compacted) return cookie;
    return local;
  }

  function fitsCookie(payload) {
    return encodeURIComponent(JSON.stringify(payload)).length <= COOKIE_VALUE_LIMIT;
  }

  function clearTouch(payload, key) {
    payload[key] = null;
    payload[key + '_meta'] = null;
    payload[key + '_raw_params'] = {};
  }

  function stripLanding(meta) {
    if (meta && typeof meta === 'object') meta.landing_url = null;
  }

  function cookiePayload(payload) {
    if (fitsCookie(payload)) return payload;

    // This is a separate copy: localStorage retains the complete evidence.
    var compact = {};
    var fallbackFields = [
      'attribution_schema_version', 'lmj_user_id', 'lmj_session_id',
      'user_first_landing_url', 'session_entry_url',
      'session_entry_referrer_external', 'session_last_activity_at',
      'first_touch', 'last_touch', 'first_touch_meta', 'last_touch_meta',
      'last_google_ads_touch', 'last_capture_warning', 'referrer', 'consentMeta', 'device',
      'created_at', 'updated_at', 'storage_written_at', 'attribution'
    ];
    fallbackFields.forEach(function (key) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) compact[key] = payload[key];
    });
    compact = JSON.parse(JSON.stringify(compact));
    compact.storage_compacted = true;
    compact.first_touch_raw_params = {};
    compact.last_touch_raw_params = {};
    if (compact.last_google_ads_touch) compact.last_google_ads_touch.raw_params = {};
    if (compact.last_capture_warning) compact.last_capture_warning.raw_params = {};
    if (fitsCookie(compact)) return compact;

    compact.user_first_landing_url = '';
    compact.session_entry_url = '';
    compact.session_entry_referrer_external = '';
    compact.referrer = null;
    stripLanding(compact.first_touch_meta);
    stripLanding(compact.last_touch_meta);
    if (compact.last_google_ads_touch) stripLanding(compact.last_google_ads_touch.meta);
    if (compact.last_capture_warning) stripLanding(compact.last_capture_warning.meta);
    delete compact.device;
    if (fitsCookie(compact)) return compact;

    // Never shorten a click ID or combine pieces from different touches.
    // If complete records cannot all fit, latest Google Ads evidence takes
    // priority. Explicit nulls replace omitted records, including their meta.
    clearTouch(compact, 'first_touch');
    if (fitsCookie(compact)) return compact;
    clearTouch(compact, 'last_touch');
    if (fitsCookie(compact)) return compact;
    // Long optional campaign labels must not displace an otherwise usable
    // Google click identifier. Keep source/medium and the complete click IDs.
    if (compact.last_google_ads_touch && compact.last_google_ads_touch.params) {
      var essentialGoogleParams = {};
      ['gclid', 'gbraid', 'wbraid', 'utm_source', 'utm_medium'].forEach(function (key) {
        var params = compact.last_google_ads_touch.params;
        if (Object.prototype.hasOwnProperty.call(params, key)) essentialGoogleParams[key] = params[key];
      });
      compact.last_google_ads_touch.params = essentialGoogleParams;
      if (fitsCookie(compact)) return compact;
    }
    compact.last_google_ads_touch = null;
    if (fitsCookie(compact)) return compact;

    // Unexpectedly large essential fields must not resurrect an older cookie.
    return null;
  }

  function removeCookie() {
    try {
      document.cookie = COOKIE_NAME + '=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    } catch (e) {}
  }

  function setCookie(payload) {
    if (!payload) {
      removeCookie();
      return;
    }
    try {
      var serialized = JSON.stringify(payload);
      var expires = new Date(Date.now() + COOKIE_DAYS * 86400000).toUTCString();
      document.cookie = COOKIE_NAME + '=' + encodeURIComponent(serialized) +
        '; expires=' + expires + '; path=/; SameSite=Lax';
      // Browsers may reject cookie writes silently, including quota failures.
      if (getCookie(COOKIE_NAME) !== serialized) removeCookie();
    } catch (e) {
      removeCookie();
    }
  }

  function storePayload(payload) {
    var stored = JSON.parse(JSON.stringify(payload));
    var candidates = getCandidates();
    lastWriteAt = Math.max(Date.now(), lastWriteAt + 1,
      writeTime(candidates.local) + 1, writeTime(candidates.cookie) + 1);
    stored.storage_written_at = lastWriteAt;

    try {
      localStorage.setItem(COOKIE_NAME, JSON.stringify(stored));
    } catch (e) {
      log('[Attribution][Storage] localStorage unavailable');
      // If reads remain available despite a failed write, an obsolete local
      // copy must not compete with the new cookie on a subsequent page.
      try { localStorage.removeItem(COOKIE_NAME); } catch (ignored) {}
    }
    setCookie(cookiePayload(stored));
  }

  function storeSessionOnly(payload) {
    storePayload(Object.assign({}, payload, { attribution: null }));
  }

  return {
    storePayload: storePayload,
    storeSessionOnly: storeSessionOnly,
    read: read
  };
})();